import { z } from 'zod';
import { topologyDigestSchema } from "./venue-map-schemas.js";
/**
 * Event seating configuration — what a room is selling, for one session.
 *
 * The companion to `venue-map-schemas.ts`, and deliberately a separate file
 * because it is a separate authority (ADR 0036). Three of them meet here, and
 * conflating any two is the failure this slice exists to avoid:
 *
 *   venue map           which chairs physically exist, and where
 *   seating config      which of them this session sells, as what — static
 *   inventory + claims  what is held or sold right now — the only counter
 *
 * The third line never becomes a stored field on the second. A configuration
 * row says `SELLABLE`; it never says `AVAILABLE`, `HELD` or `SOLD`, because
 * those are answers inventory owns and a configuration that mirrored them would
 * be the second capacity authority ADR 0015 exists to prevent.
 *
 * Availability is **session-scoped**: the same physical chair is legitimately
 * sold for Friday and again for Saturday, and never twice for Friday.
 */
// --- Identity ---------------------------------------------------------------
/**
 * A session has at most one seating configuration.
 *
 * Deliberately no `code`: two parallel commercial plans for one night would
 * mean two authorities answering "can this chair be sold", and the no-oversell
 * proof would have to reconcile them. Version history lives beneath this single
 * identity instead.
 */
export const seatingConfigurationNameSchema = z.string().trim().min(1).max(120);
/**
 * The server's canonical digest of a configuration version.
 *
 * Same rules as the topology digest whose shape it borrows: computed by the
 * server over a normalised projection of what is persisted, never over
 * submitted bytes, carried by a client only to hand back as a precondition.
 * Served as the `ETag`.
 */
export const configurationDigestSchema = topologyDigestSchema;
// --- Lifecycle --------------------------------------------------------------
/**
 *   DRAFT       the only editable state
 *   ACTIVE      sale-ready; frozen
 *   SUPERSEDED  replaced by a later active version, still the meaning of
 *               everything held or sold against it
 *   WITHDRAWN   a draft abandoned before it became commercial authority
 *
 * No route back to DRAFT, for the reason a published map version has none: a
 * claim taken under this configuration must keep meaning what it meant.
 */
export const seatingConfigurationLifecycleSchema = z.enum([
    'DRAFT',
    'ACTIVE',
    'SUPERSEDED',
    'WITHDRAWN',
]);
/**
 * A chair's **static** commercial disposition in this configuration.
 *
 * Named `saleDisposition` rather than a state, because the tempting mistake is
 * to grow it into one. It answers "may this chair be offered at all", once, at
 * authoring time. It never answers "is it free right now".
 *
 *   SELLABLE   offered, against a named product version and allocation
 *   EXCLUDED   deliberately kept off sale — a commercial choice, reversible in
 *              the next configuration version
 *   BLOCKED    unusable this night — production, sightline, safety
 *
 * The last two are separated because they answer different questions later. A
 * SELLABLE chair may still be held or sold; that is the claim state's business.
 */
export const saleDispositionSchema = z.enum(['SELLABLE', 'EXCLUDED', 'BLOCKED']);
/**
 * A chair's **dynamic** claim state, derived from the one inventory authority.
 *
 * Never stored on a configuration row and never sent by a client. Exclusivity
 * holds while `HELD` **or** `COMMITTED`: a successful purchase must not free
 * the chair it just sold.
 */
export const seatClaimStateSchema = z.enum(['NONE', 'HELD', 'COMMITTED']);
/**
 * What a surface renders, projected by the server from the two above.
 *
 * A projection, not a stored authority. It exists so a client never has to
 * combine a disposition with a claim state and risk its own interpretation.
 */
export const seatAvailabilitySchema = z.enum(['AVAILABLE', 'HELD', 'SOLD', 'EXCLUDED', 'BLOCKED']);
/**
 * How a table is sold.
 *
 * One mode per table per configuration version, and no mixed policy in R1.
 *
 *   WHOLE_TABLE       the table is the commercial unit. Acquiring it takes one
 *                     ordinary inventory hold of quantity = constituent seats,
 *                     and one exclusive claim per constituent chair — capacity
 *                     stays expressed in seats even when the customer buys a
 *                     table.
 *   INDIVIDUAL_SEATS  each constituent chair carries its own mapping.
 *
 * The two shapes are mutually exclusive, so a whole-table claim and a claim on
 * one of its chairs collide on the ordinary per-session seat invariant rather
 * than needing a rule of their own.
 */
export const tableSaleModeSchema = z.enum(['WHOLE_TABLE', 'INDIVIDUAL_SEATS']);
// --- Configuration document -------------------------------------------------
/**
 * One chair's commercial configuration.
 *
 * Keyed on the **version-scoped occurrence**. The durable identity is carried
 * too, on the read side, but a client never supplies it: occurrence is what the
 * organizer configured, identity is what the server derives from it.
 */
export const seatOfferSchema = z.object({
    venueMapSeatId: z.string().uuid(),
    saleDisposition: saleDispositionSchema,
    /**
     * The product an organizer chose. Required when SELLABLE, forbidden otherwise.
     *
     * A **product**, not a version. The server resolves the product's applicable
     * immutable version and freezes it on the configuration, so price, currency
     * and terms cannot move under a chair somebody has bought (ADR 0014) without
     * making an organizer reason about version mechanics.
     *
     * In R1 a reserved-seating product belongs to exactly one session. A
     * three-night run uses three products — Friday Reserved Seat, Saturday
     * Reserved Seat, Sunday Reserved Seat — which may share a name, a
     * description and a price and are still distinct commercial products,
     * because their inventory and seat authority are session-specific.
     *
     * Per *product* rather than per version because two certified constraints
     * make the finer grain impossible: a product has one current version at a
     * time, and one saleable allocation per product.
     */
    ticketProductId: z.string().uuid().nullish(),
    /** The allocation this chair draws against. Session-scoped for reserved seating. */
    inventoryAllocationId: z.string().uuid().nullish(),
    /** Operator-facing note for an EXCLUDED or BLOCKED chair. */
    reason: z.string().trim().min(1).max(200).nullish(),
});
export const tableOfferSchema = z.object({
    venueMapTableId: z.string().uuid(),
    saleMode: tableSaleModeSchema,
    /** Required for WHOLE_TABLE, forbidden for INDIVIDUAL_SEATS. A product. */
    ticketProductId: z.string().uuid().nullish(),
    inventoryAllocationId: z.string().uuid().nullish(),
});
/**
 * A complete commercial configuration for one session.
 *
 * Whole-document replace into a draft, as in 10A. Seat groups do **not** appear
 * here: they are an authoring convenience CC2 expands client-side into concrete
 * per-seat offers before saving, so that at activation every sellable chair has
 * exactly one resolved mapping and no precedence rule has to be invented.
 */
export const seatingConfigurationDocumentSchema = z.object({
    /** The published map version being configured. Never a draft one. */
    venueMapVersionId: z.string().uuid(),
    seats: z.array(seatOfferSchema).max(100_000).default([]),
    tables: z.array(tableOfferSchema).max(2_000).default([]),
});
// --- Requests ---------------------------------------------------------------
export const createSeatingConfigurationRequestSchema = z.object({
    eventSessionId: z.string().uuid(),
    displayName: seatingConfigurationNameSchema,
});
/**
 * Open a draft.
 *
 * `cloneFromVersionId` may name another session's configuration version — that
 * is how "same layout, Saturday too" is said. Seats and modes copy; inventory
 * and product mappings do not, because each session draws against its own.
 *
 * `venueMapVersionId` may be chosen freely **only until this session's first
 * activation**. After that the session's map version is pinned and a later
 * draft inherits it; moving to a newer map needs the re-seat tranche.
 */
export const createConfigurationDraftRequestSchema = z.object({
    cloneFromVersionId: z.string().uuid().nullish(),
    venueMapVersionId: z.string().uuid().nullish(),
});
/**
 * Replace a draft's configuration, with an optimistic precondition.
 *
 * Arrives over HTTP as `If-Match: "<digest>"`. No opt-out, and no merge.
 */
export const saveSeatingConfigurationRequestSchema = z.object({
    configuration: seatingConfigurationDocumentSchema,
    expectedConfigurationDigest: configurationDigestSchema,
});
// --- Reserved-seat inventory provisioning -----------------------------------
/** A pool's stable machine name, matching the existing inventory model. */
export const inventoryPoolCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .min(2)
    .max(39)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'Use capitals, numbers and underscores, starting with a letter.');
/**
 * Create the inventory a session's reserved seating draws on.
 *
 * Purpose-specific rather than an option on the generic inventory API. Reserved
 * seating needs a pool scoped to one session, a saleable allocation on it, and
 * that allocation marked seat-aware — three facts that are only ever correct
 * together. Exposing them as optional fields on the general command would make
 * every wrong combination expressible, and most combinations are wrong.
 *
 * `allocationMode` is deliberately absent. That this command creates
 * reserved-seat authority is what decides it; letting a caller choose would let
 * a caller choose incorrectly.
 */
export const provisionSeatingInventoryRequestSchema = z.object({
    /** The product these seats sell as. Must belong to this session alone. */
    ticketProductId: z.string().uuid(),
    /** Stable machine name for the pool, in the same alphabet as other codes. */
    code: inventoryPoolCodeSchema,
    displayName: z.string().trim().min(1).max(120),
    /**
     * How many chairs this inventory covers.
     *
     * Activation independently proves this equals the concrete sellable chairs
     * mapped to it, so a number that disagrees with the room is caught before
     * anything goes on sale rather than discovered by a customer.
     */
    capacity: z.number().int().min(1).max(1_000_000),
    /** Optional physical space, matching the existing pool model. */
    venueSpaceId: z.string().uuid().nullish(),
});
/**
 * What an organizer needs to configure seats against this inventory.
 *
 * The allocation identifier is the one a seat offer names, so this is the
 * bridge between provisioning and configuring.
 */
export const seatingInventorySchema = z.object({
    inventoryPoolId: z.string().uuid(),
    inventoryAllocationId: z.string().uuid(),
    eventSessionId: z.string().uuid(),
    ticketProductId: z.string().uuid(),
    /** The version frozen for these terms. */
    ticketProductVersionId: z.string().uuid(),
    code: z.string(),
    displayName: z.string(),
    capacity: z.number().int().min(0),
});
export const seatingInventoryListSchema = z.object({
    inventory: z.array(seatingInventorySchema),
});
// --- Availability projections ----------------------------------------------
/**
 * Server-computed counts. A client renders these and never derives them.
 *
 * Two reasons this is not negotiable. A browser summing a seat array would be a
 * second capacity authority that can disagree with the first. And under
 * level-of-detail the client does not *have* every chair — a count derived from
 * what happens to be rendered would quietly understate a hundred-thousand-seat
 * room.
 */
export const seatingCountsSchema = z.object({
    configured: z.number().int().min(0),
    sellable: z.number().int().min(0),
    excluded: z.number().int().min(0),
    blocked: z.number().int().min(0),
    available: z.number().int().min(0),
    held: z.number().int().min(0),
    committed: z.number().int().min(0),
});
export const sectionCountsSchema = z.object({
    venueMapSectionId: z.string().uuid(),
    counts: seatingCountsSchema,
});
export const tableCountsSchema = z.object({
    venueMapTableId: z.string().uuid(),
    saleMode: tableSaleModeSchema,
    /** For WHOLE_TABLE, whether the table as a unit can still be taken. */
    tableAvailability: seatAvailabilitySchema,
    counts: seatingCountsSchema,
});
/**
 * Aggregates at every level a surface needs, computed once by the server.
 */
export const seatingAvailabilitySummarySchema = z.object({
    configuration: seatingCountsSchema,
    sections: z.array(sectionCountsSchema),
    tables: z.array(tableCountsSchema),
});
/**
 * One chair, as a surface needs it.
 *
 * Both identifiers, for different jobs — and neither substitutes for the other:
 *
 *   venueMapSeatId          the occurrence. What this chair meant in this map
 *                           version. The overlay key, and the historical record.
 *   venueMapSeatIdentityId  the durable chair. The per-session exclusion key,
 *                           and what a later re-seat migration will follow.
 *
 * A client keeps the identity but must never use it to reinterpret a chair
 * under a different map version.
 */
export const configuredSeatSchema = z.object({
    venueMapSeatId: z.string().uuid(),
    venueMapSeatIdentityId: z.string().uuid(),
    saleDisposition: saleDispositionSchema,
    claimState: seatClaimStateSchema,
    /** The projection. Derived by the server from the two fields above. */
    availability: seatAvailabilitySchema,
    /** What the organizer chose. */
    ticketProductId: z.string().uuid().nullable(),
    /**
     * What the server froze. The terms this chair is actually offered on.
     *
     * A later version of the same product never reinterprets an active
     * configuration; the frozen version is what was configured and what was sold.
     */
    ticketProductVersionId: z.string().uuid().nullable(),
    inventoryAllocationId: z.string().uuid().nullable(),
    /** Set when this chair belongs to a table, so an overlay can group it. */
    venueMapTableId: z.string().uuid().nullable(),
    reason: z.string().nullable(),
});
// --- Responses --------------------------------------------------------------
export const seatingConfigurationVersionSummarySchema = z.object({
    configurationVersionId: z.string().uuid(),
    versionNumber: z.number().int().min(1),
    lifecycle: seatingConfigurationLifecycleSchema,
    venueMapVersionId: z.string().uuid(),
    venueMapVersionNumber: z.number().int().min(1),
    configurationDigest: configurationDigestSchema,
    createdAt: z.string(),
    activatedAt: z.string().nullable(),
    supersededAt: z.string().nullable(),
    withdrawnAt: z.string().nullable(),
});
export const seatingConfigurationSummarySchema = z.object({
    seatingConfigurationId: z.string().uuid(),
    eventId: z.string().uuid(),
    eventSessionId: z.string().uuid(),
    displayName: z.string(),
    createdAt: z.string(),
    draftVersion: seatingConfigurationVersionSummarySchema.nullable(),
    activeVersion: seatingConfigurationVersionSummarySchema.nullable(),
    /**
     * The map version this session is pinned to, once it has ever been activated.
     *
     * Null before the first activation, when a draft may still choose freely.
     */
    pinnedVenueMapVersionId: z.string().uuid().nullable(),
    /**
     * True when a newer published map version exists than the pinned one.
     *
     * Informational only, and deliberately the whole of the API surface for it:
     * an active configuration never migrates on its own, and 10B has no migration
     * workflow, so a surface may say a newer map exists and has nothing to call
     * to act on it.
     */
    newerVenueMapVersionAvailable: z.boolean(),
});
export const seatingConfigurationDetailSchema = seatingConfigurationSummarySchema.extend({
    versions: z.array(seatingConfigurationVersionSummarySchema),
});
export const configuredTableSchema = z.object({
    venueMapTableId: z.string().uuid(),
    saleMode: tableSaleModeSchema,
    ticketProductId: z.string().uuid().nullable(),
    ticketProductVersionId: z.string().uuid().nullable(),
    inventoryAllocationId: z.string().uuid().nullable(),
});
/**
 * A configuration version and what a whole-room view needs.
 *
 * Deliberately **without** a per-seat array. At a hundred thousand chairs that
 * array measured 40 MiB of JSON — larger than the document that created it,
 * because every chair repeated five identifiers — and it was sent on every
 * load. Roughly half a minute on a 10 Mbps venue connection before any browser
 * work, to render a room in which no individual chair is legible.
 *
 * A whole-room view needs aggregates, and aggregates are here. Per-chair state
 * is fetched for the chairs actually being drawn, through
 * `seatDetailRequestSchema` below.
 */
export const seatingConfigurationViewSchema = z.object({
    seatingConfigurationId: z.string().uuid(),
    version: seatingConfigurationVersionSummarySchema,
    tables: z.array(configuredTableSchema),
    summary: seatingAvailabilitySummarySchema,
});
// --- The editable draft document --------------------------------------------
/**
 * One configured chair, as the editor needs it back.
 *
 * Everything static, and nothing dynamic. This is what makes a draft
 * round-trip: an editor that reconstructed the commercial terms from the venue
 * map would silently drop the product, the frozen version, the allocation and
 * the reason, and the save that followed would erase them for every chair it
 * had not touched.
 *
 * `ticketProductVersionId` is present on the read and absent from the write.
 * The organizer chose a product; the server froze the version. Returning it
 * lets a surface show the terms actually in force without asking the editor to
 * send back something it does not own.
 */
export const configuredSeatOfferSchema = z.object({
    venueMapSeatId: z.string().uuid(),
    saleDisposition: saleDispositionSchema,
    ticketProductId: z.string().uuid().nullable(),
    ticketProductVersionId: z.string().uuid().nullable(),
    inventoryAllocationId: z.string().uuid().nullable(),
    reason: z.string().nullable(),
});
/**
 * The whole editable document, for a draft and only a draft.
 *
 * Large by nature — roughly 21 MiB at a hundred thousand chairs — and fetched
 * only when somebody deliberately opens a draft to edit it, never on an
 * ordinary view. That is the trade R1 makes: the write is a whole-document
 * replace, so an editor needs the whole document in hand to save safely, and
 * splitting the read into chunks would move the same bytes while adding
 * partial-assembly and stale-chunk failure modes a patch-based write does not
 * yet exist to justify.
 *
 * Served with the canonical configuration digest as its `ETag` — the same
 * value `If-Match` protects on save, from the same code path, so what an editor
 * loaded and what it must present back cannot drift apart.
 */
export const seatingConfigurationDocumentViewSchema = z.object({
    configurationVersionId: z.string().uuid(),
    lifecycle: seatingConfigurationLifecycleSchema,
    venueMapVersionId: z.string().uuid(),
    configurationDigest: configurationDigestSchema,
    seats: z.array(configuredSeatOfferSchema),
    tables: z.array(configuredTableSchema),
});
// --- Bounded seat detail ----------------------------------------------------
/**
 * The most chairs one detail request may name.
 *
 * Large enough for any working zoom — a legible screen of a stadium is a few
 * hundred chairs, and a dense one under a thousand — and small enough that the
 * response stays in the range already shown to behave. A renderer drawing 936
 * chairs asks for 936, not for the room.
 */
export const SEAT_DETAIL_MAX = 5_000;
/**
 * Dynamic state for named chairs.
 *
 * A POST because thousands of identifiers do not belong in a URL, and pretending
 * otherwise would mean either a query string nothing along the path is obliged
 * to carry or an artificial page size. It is a read: it changes nothing.
 *
 * Occurrences, never durable identities. The occurrence is what this chair means
 * in the map version being rendered; the identity spans versions and would let
 * a stale client ask about a chair as it exists somewhere else.
 */
/**
 * The most raw entries one request may carry, before duplicates collapse.
 *
 * Deliberately larger than `SEAT_DETAIL_MAX`. The limit that matters is on
 * *unique* chairs, and a renderer assembling a visibility set from overlapping
 * tiles will name some of them twice — refusing a list of five thousand unique
 * chairs for having sent one of them again would be arithmetic pedantry, not a
 * protection. This bound exists only so the deduplication itself cannot be
 * handed something absurd.
 */
export const SEAT_DETAIL_RAW_MAX = SEAT_DETAIL_MAX * 4;
/**
 * The contract, stated once and unambiguously:
 *
 *   4,000 entries / 4,000 unique   accepted
 *   10,000 entries / 5,000 unique  accepted, deduplicated
 *   5,001 unique                   refused
 *   anything                       never silently truncated
 *
 * The array bound below is the raw one. The rule that decides acceptance is on
 * unique identifiers and lives in the service, because it is a statement about
 * how many chairs were asked about, not about how long the list was.
 */
export const seatDetailRequestSchema = z.object({
    venueMapSeatIds: z.array(z.string().uuid()).min(1).max(SEAT_DETAIL_RAW_MAX),
});
/**
 * What can actually change about a chair.
 *
 * Four fields, and no repeated identifiers. Product, frozen product version,
 * allocation and durable identity are static configuration facts a client
 * already holds; sending them again per chair is what made the old projection
 * larger than the document that produced it.
 *
 * `saleDisposition` is here despite being static, because it costs one short
 * string and without it a renderer could not colour an excluded chair without a
 * second lookup — which would defeat the point of a bounded request.
 */
export const seatDetailSchema = z.object({
    venueMapSeatId: z.string().uuid(),
    saleDisposition: saleDispositionSchema,
    claimState: seatClaimStateSchema,
    availability: seatAvailabilitySchema,
});
/**
 * The answer to exactly what was asked.
 *
 * Never silently truncated. A response of five thousand means these five
 * thousand, not the first five thousand of eighty — a renderer that painted a
 * quietly shortened answer would show a room that is wrong rather than a room
 * that is loading. Chairs the configuration does not offer are simply absent,
 * and `requested` lets a client see that without counting.
 */
export const seatDetailResponseSchema = z.object({
    requested: z.number().int().min(0),
    seats: z.array(seatDetailSchema),
});
// --- Refusal reasons --------------------------------------------------------
/**
 * Why a seating-configuration command was refused.
 *
 * Same discipline as the venue-map reasons: a code exists only where the
 * *remedy* differs. A client switches on these and never reads the message.
 *
 * A stale save is not in this list. It is `SEATING_CONFIGURATION_STALE`, a
 * top-level error code with a 412, so a client branches on the business event
 * rather than on a status any future precondition could also produce.
 * `asSeatingConfigurationRefusalReason` returns null for it, deliberately.
 */
export const SEATING_CONFIGURATION_REFUSAL_REASONS = [
    /**
     * The named map version is not published. A draft or withdrawn map has no
     * settled meaning to sell against.
     */
    'SEATING_MAP_VERSION_NOT_PUBLISHED',
    /**
     * This session has already activated a configuration against a different map
     * version. The session's map version is pinned; moving it needs the re-seat
     * migration tranche, which does not exist yet.
     */
    'SEATING_MAP_VERSION_PINNED',
    /** The configuration is not a draft. Remedy: open a new draft version. */
    'SEATING_CONFIGURATION_NOT_DRAFT',
    /**
     * A draft is already open. Details carry `configurationVersionId` and
     * `versionNumber` so a client can link to it rather than send somebody
     * hunting.
     */
    'SEATING_OPEN_DRAFT_EXISTS',
    /**
     * A chair carries an individual mapping while its table is WHOLE_TABLE, or a
     * table is WHOLE_TABLE while its chairs carry individual mappings.
     */
    'SEATING_TABLE_MODE_CONFLICT',
    /** A chair or table named here does not belong to the pinned map version. */
    'SEATING_SEAT_NOT_IN_MAP_VERSION',
    /**
     * The allocation belongs to a different EventSession. That and nothing else:
     * telling somebody to "use this night's inventory" when their seats merely
     * span two otherwise-valid allocations sends them to fix the wrong thing.
     * Remedy: choose inventory provisioned for this session.
     */
    'SEATING_INVENTORY_NOT_FOR_SESSION',
    /**
     * One atomic acquisition draws on one allocation in R1, and these seats span
     * two. Both may be perfectly valid for this session.
     * Remedy: choose seats drawing on the same reserved-seat inventory, or
     * acquire the groups separately.
     */
    'SEATING_ACQUISITION_SPANS_ALLOCATIONS',
    /**
     * An allocation's capacity and the concrete sellable chairs mapped to it
     * disagree. Ninety units behind a hundred chairs sells out with ten visibly
     * empty; a hundred and ten oversells a room that cannot seat them.
     * Remedy: change the capacity, or the chairs, until they match.
     */
    'SEATING_ALLOCATION_CAPACITY_MISMATCH',
    /**
     * The product admits to more than one session, or to a different one. R1
     * reserved seating needs a product belonging to exactly this session — three
     * nights means three products, which may share a name and a price.
     * Remedy: choose or create this session's own reserved-seat product.
     */
    'SEATING_PRODUCT_NOT_SINGLE_SESSION',
    /**
     * This session and product already have reserved-seat inventory, and the
     * request asks for it on different terms — another capacity, another code.
     *
     * A replay of the same intent converges silently and returns the original
     * identifiers; this is the case where the intent genuinely differs, and
     * reinterpreting live capacity because a retry disagreed would be the worst
     * possible reading of an ambiguous request.
     * Remedy: use the existing inventory, or change it deliberately.
     */
    'SEATING_INVENTORY_ALREADY_PROVISIONED',
    /**
     * This product is already the reserved-seat authority for another session.
     * One product, one session: its current version and its single saleable
     * allocation cannot serve two nights at once.
     * Remedy: a separate product for this session, same name and price if wanted.
     */
    'SEATING_PRODUCT_ALREADY_BOUND_TO_SESSION',
    /**
     * The product has no version that can be offered — none current, or the
     * current one does not admit this session.
     */
    'SEATING_PRODUCT_HAS_NO_APPLICABLE_VERSION',
    /**
     * A seat or table offered for sale carries no reserved-seat inventory, or no
     * product to sell it as. Both halves are required together: a chair on sale
     * with nothing behind it is a chair somebody could take without consuming any
     * capacity.
     *
     * One reason for the seat case and the whole-table case, because the remedy
     * is the same sentence in both — give it a product and inventory, or take it
     * off sale. Details name the offer.
     */
    'SEATING_SELLABLE_REQUIRES_INVENTORY',
    /**
     * A seat-detail request named more chairs than one request may carry.
     * Remedy: split the set. The limit exists so a response is never quietly
     * shortened, which would paint a room that is wrong rather than loading.
     */
    'SEATING_SEAT_DETAIL_TOO_LARGE',
    /** Activation was asked for on a configuration offering nothing sellable. */
    'SEATING_CONFIGURATION_HAS_NO_OFFERS',
    /**
     * Activation was asked for while chairs are held under the current active
     * configuration. Remedy: wait for the holds to expire or release them.
     */
    'SEATING_LIVE_HOLDS_PRESENT',
    /**
     * A superseding activation was asked for after chairs have been sold. What
     * those sales mean cannot be reinterpreted until the re-seat tranche exists.
     */
    'SEATING_COMMITMENTS_PRESENT',
];
export const seatingConfigurationRefusalReasonSchema = z.enum(SEATING_CONFIGURATION_REFUSAL_REASONS);
/**
 * Narrows a refusal a client recognises, or `null`.
 *
 * Null rather than throwing, so a surface several releases behind a new reason
 * falls back to the generic refusal instead of matching nothing.
 */
export function asSeatingConfigurationRefusalReason(value) {
    const parsed = seatingConfigurationRefusalReasonSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
//# sourceMappingURL=seating-configuration-schemas.js.map